/**
 * Module: `ui-helper/messages.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

import type { UIMessage } from "@tanstack/ai-react";
import type { Message, MessageError } from "@/types";
import { MessageId } from "@/types/branded";
import {
  gatekeeperMetadataToMessageFields,
  messageFieldsToGatekeeperMetadataPatch,
  readGatekeeperMetadataPart,
  withGatekeeperMetadata,
} from "@/lib/qualification-lifecycle/metadata-part";
import { resolveAgentMessageDisplayTimestamp } from "@/lib/ui-helper/dates";

/**
 * Extract the concatenated text content from a TanStack AI UIMessage.
 * Handles the parts-based message format where text lives in TextPart entries.
 */
function getTextContent(msg: UIMessage): string {
  return msg.parts
    .filter((p): p is { type: "text"; content: string } => p.type === "text")
    .map((p) => p.content)
    .join("");
}

/**
 * Extract thinking/chain-of-thought content from a UIMessage.
 * Returns empty string if no thinking parts exist.
 */
// function getThinkingContent(msg: UIMessage): string {
//   return msg.parts
//     .filter(
//       (p): p is { type: "thinking"; content: string } => p.type === "thinking"
//     )
//     .map((p) => p.content)
//     .join("");
// }

/**
 * Metadata key used to stash status/error/completionDurationMs on UIMessage for round-tripping.
 * TanStack AI UIMessage doesn't natively have these fields, so we attach them
 * via a custom part that is filtered out when reading text content.
 */
const STATUS_PART_TYPE = "status-metadata" as const;

interface StatusPart {
  type: typeof STATUS_PART_TYPE;
  status?: "created" | "succeeded" | "failed" | "cancelled";
  error?: MessageError;
  /** Time to generate this assistant answer (ms). Preserved when loading from API. */
  completionDurationMs?: number;
}

/**
 * Convert a domain Message (flat content string) to a TanStack AI UIMessage.
 * Used when loading messages from React Query cache / Dust API responses.
 */
export function messageToUIMessage(msg: Message): UIMessage {
  const parts: UIMessage["parts"] = [
    { type: "text" as const, content: msg.content },
  ];

  // Stash status/error/completionDurationMs as a custom part so uiMessageToMessage can recover them
  if (msg.status || msg.error || msg.completionDurationMs != null) {
    (parts as unknown[]).push({
      type: STATUS_PART_TYPE,
      status: msg.status,
      error: msg.error,
      completionDurationMs: msg.completionDurationMs,
    } satisfies StatusPart);
  }

  let uiMessage: UIMessage = {
    id: String(msg.id),
    role: msg.role,
    parts,
    createdAt: msg.timestamp,
  };

  const gatekeeperPatch = messageFieldsToGatekeeperMetadataPatch(msg);
  if (Object.keys(gatekeeperPatch).length > 0) {
    uiMessage = withGatekeeperMetadata(uiMessage, gatekeeperPatch);
  }

  return uiMessage;
}

function findLastAssistantIndex(messages: UIMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") return i;
  }
  return -1;
}

/**
 * Ensure the last assistant UIMessage has at least `content` as text (stream may
 * finish before TanStack AI parts are fully flushed).
 */
export function withLastAssistantTextFromContent(
  messages: UIMessage[],
  content: string
): UIMessage[] {
  const trimmed = content.trim();
  if (!trimmed) return messages;

  const lastIdx = findLastAssistantIndex(messages);
  if (lastIdx < 0) return messages;

  const msg = messages[lastIdx];
  const currentText = (msg.parts ?? [])
    .filter((p): p is { type: "text"; content: string } => p.type === "text")
    .map((p) => p.content)
    .join("");
  if (currentText.length >= trimmed.length) return messages;

  const updated = [...messages];
  updated[lastIdx] = {
    ...msg,
    parts: [
      ...(msg.parts?.filter((p) => p.type !== "text") ?? []),
      { type: "text" as const, content: trimmed },
    ],
  };
  return updated;
}

/**
 * Attach generation duration (and completion display time) to the last assistant message.
 * Call before persist so the cached row keeps completionDurationMs and finished-at timestamp.
 */
export function withCompletionDurationOnLastAssistant(
  messages: UIMessage[],
  durationMs: number,
  options?: { completedTs?: number | null }
): UIMessage[] {
  const lastIdx = findLastAssistantIndex(messages);
  if (lastIdx < 0) return messages;

  const msg = messages[lastIdx];
  const partsWithoutStatus = (msg.parts ?? []).filter(
    (p) =>
      typeof p !== "object" ||
      p === null ||
      (p as Record<string, unknown>).type !== STATUS_PART_TYPE
  );
  const statusPart: StatusPart = {
    type: STATUS_PART_TYPE,
    status: "succeeded",
    completionDurationMs: durationMs,
  };

  const created = msg.createdAt ?? new Date();
  const displayAt = resolveAgentMessageDisplayTimestamp({
    created,
    completedTs: options?.completedTs,
    completionDurationMs: durationMs,
  });

  const updated = [...messages];
  updated[lastIdx] = {
    ...msg,
    createdAt: displayAt,
    parts: [...partsWithoutStatus, statusPart] as UIMessage["parts"],
  };
  return updated;
}

export function uiMessageToMessage(msg: UIMessage): Message {
  // Recover status metadata if present
  const statusPart = (msg.parts as unknown[]).find(
    (p): p is StatusPart =>
      typeof p === "object" &&
      p !== null &&
      (p as Record<string, unknown>).type === STATUS_PART_TYPE
  );

  const gatekeeperPart = readGatekeeperMetadataPart(msg);

  return {
    id: MessageId(msg.id),
    role: msg.role as "user" | "assistant",
    content: getTextContent(msg),
    timestamp: msg.createdAt ?? new Date(),
    status: statusPart?.status,
    error: statusPart?.error,
    completionDurationMs: statusPart?.completionDurationMs,
    ...gatekeeperMetadataToMessageFields(gatekeeperPart),
  };
}

/**
 * Build a UIMessage for an assistant reply that failed (e.g. Dust API rate limit).
 * Used when the chat POST returns an error before any stream chunks arrive.
 */
export function createFailedAssistantUIMessage(error: MessageError): UIMessage {
  const parts: unknown[] = [
    { type: "text" as const, content: "" },
    {
      type: STATUS_PART_TYPE,
      status: "failed" as const,
      error,
    } satisfies StatusPart,
  ];
  return {
    id: `failed-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    role: "assistant",
    parts: parts as UIMessage["parts"],
    createdAt: new Date(),
  };
}

/**
 * Add failed status/error to an existing assistant UIMessage (e.g. placeholder).
 * Use when the stream failed after the processor already added an assistant message.
 */
/** Attach cancelled status to an assistant UIMessage (e.g. user clicked Stop). */
export function withCancelledStatus(msg: UIMessage): UIMessage {
  const existingParts = msg.parts ?? [];
  const hasStatusPart = (existingParts as unknown[]).some(
    (p) =>
      typeof p === "object" &&
      p !== null &&
      (p as Record<string, unknown>).type === STATUS_PART_TYPE
  );
  if (hasStatusPart) return msg;
  const statusPart: StatusPart = {
    type: STATUS_PART_TYPE,
    status: "cancelled",
  };
  return {
    ...msg,
    parts: [...existingParts, statusPart] as UIMessage["parts"],
  };
}

export function withCancelledStatusOnLastAssistant(
  messages: UIMessage[],
  agentMessageId?: string | null
): UIMessage[] {
  const lastIdx = findLastAssistantIndex(messages);
  if (lastIdx < 0) return messages;

  const msg = messages[lastIdx];
  const dustId = agentMessageId?.trim();
  const resolved = dustId && msg.id !== dustId ? { ...msg, id: dustId } : msg;

  const updated = [...messages];
  updated[lastIdx] = withCancelledStatus(resolved);
  return updated;
}

export function withFailedStatus(
  msg: UIMessage,
  error: MessageError
): UIMessage {
  const existingParts = msg.parts ?? [];
  const hasStatusPart = (existingParts as unknown[]).some(
    (p) =>
      typeof p === "object" &&
      p !== null &&
      (p as Record<string, unknown>).type === STATUS_PART_TYPE
  );
  if (hasStatusPart) return msg;
  const statusPart: StatusPart = {
    type: STATUS_PART_TYPE,
    status: "failed",
    error,
  };
  return {
    ...msg,
    parts: [...existingParts, statusPart] as UIMessage["parts"],
  };
}
